import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { ChevronLeft, ChevronRight, Send } from 'lucide-react';
import Step1Veterinario from '../components/form/Step1Veterinario';
import Step2Paciente from '../components/form/Step2Paciente';
import Step3Pruebas from '../components/form/Step3Pruebas';
import Step4InfoClinica from '../components/form/Step4InfoClinica';
import logo from '../assets/amplicare-logo.svg';

const LabRequestForm = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Obtener fecha y hora actuales
  const obtenerFechaActual = () => {
    const ahora = new Date();
    const año = ahora.getFullYear();
    const mes = String(ahora.getMonth() + 1).padStart(2, '0');
    const dia = String(ahora.getDate()).padStart(2, '0');
    return `${año}-${mes}-${dia}`;
  };
  
  const obtenerHoraActual = () => {
    const ahora = new Date();
    const horas = String(ahora.getHours()).padStart(2, '0');
    const minutos = String(ahora.getMinutes()).padStart(2, '0');
    return `${horas}:${minutos}`;
  };
  
  const [formData, setFormData] = useState({
    // Paso 1: Veterinario
    fecha: obtenerFechaActual(),
    horaToma: obtenerHoraActual(),
    nombreVeterinario: '',
    clinica: '',
    telefonoVeterinario: '',
    emailVeterinario: '',
    
    // Paso 2: Paciente
    nombrePaciente: '',
    especie: '',
    edad: '',
    raza: '',
    propietario: '',
    sexo: '',
    
    // Paso 3: Pruebas
    pruebaSolicitada: '',
    observaciones: '',
    
    // Paso 4: Información Clínica
    tomaAntibioticos: '',
    fechaUltimaDosis: '',
    alergicoAntibioticos: '',
    cualAlergia: '',
    tiempoEvolucion: '',
    origenMuestra: '',
    
    // Sangre
    sangreJeringa: false,
    sangreSistemaVacio: false,
    sangreCateter: false,
    sangreOtra: '',
    
    // Orina
    orinaCistocentesisTraumatica: false,
    orinaCistocentesisNoTraumatica: false,
    orinaSondaje: false,
    orinaMiccionEspontanea: false,
    orinaOtra: '',
    
    // Hisopados
    hisopadoStuart: false,
    hisopadoAmies: false,
    hisopadoSolucionSalina: false,
    hisopadoSecoEsteril: false,
    hisopadoRefrigerado: false,
    hisopadoOtra: '',
    observacionesConservacion: '',
    
    // Cultivo Faríngea
    faringeaUlceraciones: false,
    faringeaManchasBlanquecinas: false,
    faringeaTos: false,
    faringeaAmigdalasInflamadas: false,
    faringeaNormal: false,
    
    // Exudado Diversos
    exudadoPurulento: false,
    exudadoSanguinolento: false,
    exudadoDolorManipulacion: false,
    exudadoFiebre: false,
    
    // Cultivo Nasal
    nasalEstornudos: false,
    nasalSecrecion: false,
    nasalCuadroRecurrente: false,
    nasalSinAlteraciones: false,
    
    // Coprocultivo
    coprDiarreaCronica: false,
    coprDiarreaAguda: false,
    coprDolorAbdominal: false,
    coprVomito: false,
    coprLetargia: false,
    coprFiebre: false,
    coprIngestaSospechosa: '',
    coprCualIngesta: '',
    
    // Urocultivo
    uroAntecedenteInfeccion: '',
    uroOlorFuerte: '',
    uroDolorAbdominal: false,
    uroDolorPalpacion: false,
    uroEsfuerzoOrinar: false,
    uroMiccionesFrecuentes: false,
    uroSecrecionUretral: false,
    uroOrinaTurbia: false,
    
    // Hongos
    hongosTipoLesion: '',
    hongosSitioLesion: '',
    
    // Traqueal/Bronquial
    traquealTosCronica: false,
    traquealTosReciente: false,
    traquealDificultadRespiratoria: false,
    traquealRespiracionRapida: false,
    traquealPerdidaPeso: false,
    traquealFiebre: false
  });

  const totalSteps = 4;
  const progress = (currentStep / totalSteps) * 100;

  const stepTitles = [
    'Datos del Veterinario',
    'Datos del Paciente',
    'Pruebas y Observaciones',
    'Información Clínica'
  ];

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const validateStep = () => {
    if (currentStep === 1) {
      return formData.fecha && formData.horaToma && formData.nombreVeterinario && 
             formData.clinica && formData.telefonoVeterinario && formData.emailVeterinario;
    }
    if (currentStep === 2) {
      return formData.nombrePaciente && formData.especie && formData.edad && 
             formData.raza && formData.sexo;
    }
    if (currentStep === 3) {
      return formData.pruebaSolicitada && formData.pruebaSolicitada.trim() !== '';
    }
    return true;
  };

  const handleNext = () => {
    if (validateStep()) {
      setCurrentStep(prev => Math.min(prev + 1, totalSteps));
    } else {
      alert('Por favor completa todos los campos obligatorios');
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const generateEmailHTML = () => {
    return `
<!DOCTYPE html>
<html>
<head>
  <style>
    body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
    .container { max-width: 800px; margin: 0 auto; padding: 20px; }
    .header { background: #505BB3; color: white; padding: 20px; text-align: center; border-radius: 8px 8px 0 0; }
    .section { background: #f9f9f9; padding: 15px; margin: 15px 0; border-radius: 8px; border-left: 4px solid #505BB3; }
    .section-title { color: #505BB3; font-size: 18px; font-weight: bold; margin-bottom: 10px; }
    .field { margin: 8px 0; }
    .field-label { font-weight: bold; color: #555; }
    .field-value { color: #333; margin-left: 10px; }
    .checkbox-list { margin-left: 20px; }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1>Solicitud de Pruebas de Laboratorio</h1>
      <p>Amplicare Lab</p>
    </div>
    
    <div class="section">
      <div class="section-title">📅 Información General</div>
      <div class="field"><span class="field-label">Fecha:</span><span class="field-value">${formData.fecha}</span></div>
      <div class="field"><span class="field-label">Hora de la Toma:</span><span class="field-value">${formData.horaToma}</span></div>
    </div>
    
    <div class="section">
      <div class="section-title">👨‍⚕️ Datos del Médico Veterinario</div>
      <div class="field"><span class="field-label">Nombre:</span><span class="field-value">${formData.nombreVeterinario}</span></div>
      <div class="field"><span class="field-label">Clínica:</span><span class="field-value">${formData.clinica}</span></div>
      <div class="field"><span class="field-label">Teléfono:</span><span class="field-value">${formData.telefonoVeterinario}</span></div>
      <div class="field"><span class="field-label">Email:</span><span class="field-value">${formData.emailVeterinario}</span></div>
    </div>
    
    <div class="section">
      <div class="section-title">🐾 Datos del Paciente</div>
      <div class="field"><span class="field-label">Nombre:</span><span class="field-value">${formData.nombrePaciente}</span></div>
      <div class="field"><span class="field-label">Especie:</span><span class="field-value">${formData.especie}</span></div>
      <div class="field"><span class="field-label">Edad:</span><span class="field-value">${formData.edad}</span></div>
      <div class="field"><span class="field-label">Raza:</span><span class="field-value">${formData.raza}</span></div>
      <div class="field"><span class="field-label">Propietario:</span><span class="field-value">${formData.propietario}</span></div>
      <div class="field"><span class="field-label">Sexo:</span><span class="field-value">${formData.sexo}</span></div>
    </div>
    
    <div class="section">
      <div class="section-title">🔬 Pruebas Solicitadas</div>
      <div class="field-value">${formData.pruebaSolicitada || 'No especificado'}</div>
    </div>
    
    <div class="section">
      <div class="section-title">📋 Observaciones</div>
      <div class="field-value">${formData.observaciones || 'No especificado'}</div>
    </div>
    
    <div class="section">
      <div class="section-title">💊 Datos Generales</div>
      <div class="field"><span class="field-label">Toma antibióticos actualmente:</span><span class="field-value">${formData.tomaAntibioticos || 'No especificado'}</span></div>
      ${formData.fechaUltimaDosis ? `<div class="field"><span class="field-label">Fecha última dosis:</span><span class="field-value">${formData.fechaUltimaDosis}</span></div>` : ''}
      <div class="field"><span class="field-label">Alérgico a antibióticos:</span><span class="field-value">${formData.alergicoAntibioticos || 'No especificado'}</span></div>
      ${formData.cualAlergia ? `<div class="field"><span class="field-label">Cuál:</span><span class="field-value">${formData.cualAlergia}</span></div>` : ''}
      ${formData.tiempoEvolucion ? `<div class="field"><span class="field-label">Tiempo de Evolución:</span><span class="field-value">${formData.tiempoEvolucion}</span></div>` : ''}
      ${formData.origenMuestra ? `<div class="field"><span class="field-label">Origen de la muestra:</span><span class="field-value">${formData.origenMuestra}</span></div>` : ''}
    </div>
    
    ${(formData.sangreJeringa || formData.sangreSistemaVacio || formData.sangreCateter || formData.sangreOtra) ? `
    <div class="section">
      <div class="section-title">🩸 Recolección de Sangre</div>
      <div class="checkbox-list">
        ${formData.sangreJeringa ? '<div>✓ Jeringa</div>' : ''}
        ${formData.sangreSistemaVacio ? '<div>✓ Sistema de vacío</div>' : ''}
        ${formData.sangreCateter ? '<div>✓ Muestra tomada de catéter</div>' : ''}
        ${formData.sangreOtra ? `<div>✓ Otra: ${formData.sangreOtra}</div>` : ''}
      </div>
    </div>
    ` : ''}
    
    ${(formData.orinaCistocentesisTraumatica || formData.orinaCistocentesisNoTraumatica || formData.orinaSondaje || formData.orinaMiccionEspontanea || formData.orinaOtra) ? `
    <div class="section">
      <div class="section-title">💧 Recolección de Orina</div>
      <div class="checkbox-list">
        ${formData.orinaCistocentesisTraumatica ? '<div>✓ Cistocentesis traumática</div>' : ''}
        ${formData.orinaCistocentesisNoTraumatica ? '<div>✓ Cistocentesis no traumática</div>' : ''}
        ${formData.orinaSondaje ? '<div>✓ Sondaje uretral</div>' : ''}
        ${formData.orinaMiccionEspontanea ? '<div>✓ Micción espontánea</div>' : ''}
        ${formData.orinaOtra ? `<div>✓ Otra: ${formData.orinaOtra}</div>` : ''}
      </div>
    </div>
    ` : ''}
    
    ${(formData.hisopadoStuart || formData.hisopadoAmies || formData.hisopadoSolucionSalina || formData.hisopadoSecoEsteril || formData.hisopadoRefrigerado || formData.hisopadoOtra || formData.observacionesConservacion) ? `
    <div class="section">
      <div class="section-title">🧪 Hisopados / Tejidos / Exudados</div>
      <div class="checkbox-list">
        ${formData.hisopadoStuart ? '<div>✓ Stuart</div>' : ''}
        ${formData.hisopadoAmies ? '<div>✓ Amies</div>' : ''}
        ${formData.hisopadoSolucionSalina ? '<div>✓ Solución salina fisiológica 0.9%</div>' : ''}
        ${formData.hisopadoSecoEsteril ? '<div>✓ Seco estéril</div>' : ''}
        ${formData.hisopadoRefrigerado ? '<div>✓ Refrigerado</div>' : ''}
        ${formData.hisopadoOtra ? `<div>✓ Otra: ${formData.hisopadoOtra}</div>` : ''}
      </div>
      ${formData.observacionesConservacion ? `<div class="field"><span class="field-label">Observaciones:</span><span class="field-value">${formData.observacionesConservacion}</span></div>` : ''}
    </div>
    ` : ''}
    
    ${(formData.faringeaUlceraciones || formData.faringeaManchasBlanquecinas || formData.faringeaTos || formData.faringeaAmigdalasInflamadas || formData.faringeaNormal) ? `
    <div class="section">
      <div class="section-title">🦠 Cultivo-Microscopía Faríngea/Nasofaríngea</div>
      <div class="checkbox-list">
        ${formData.faringeaUlceraciones ? '<div>✓ Ulceraciones</div>' : ''}
        ${formData.faringeaManchasBlanquecinas ? '<div>✓ Manchas blanquecinas</div>' : ''}
        ${formData.faringeaTos ? '<div>✓ Tos</div>' : ''}
        ${formData.faringeaAmigdalasInflamadas ? '<div>✓ Amígdalas inflamadas</div>' : ''}
        ${formData.faringeaNormal ? '<div>✓ Región amigdalina y faríngea normales</div>' : ''}
      </div>
    </div>
    ` : ''}
    
    ${(formData.exudadoPurulento || formData.exudadoSanguinolento || formData.exudadoDolorManipulacion || formData.exudadoFiebre) ? `
    <div class="section">
      <div class="section-title">💉 Cultivo-Microscopía Exudado Diversos</div>
      <div class="checkbox-list">
        ${formData.exudadoPurulento ? '<div>✓ Exudado purulento</div>' : ''}
        ${formData.exudadoSanguinolento ? '<div>✓ Exudado sanguinolento</div>' : ''}
        ${formData.exudadoDolorManipulacion ? '<div>✓ Dolor a la manipulación</div>' : ''}
        ${formData.exudadoFiebre ? '<div>✓ Fiebre documentada (MVZ)</div>' : ''}
      </div>
    </div>
    ` : ''}
    
    ${(formData.nasalEstornudos || formData.nasalSecrecion || formData.nasalCuadroRecurrente || formData.nasalSinAlteraciones) ? `
    <div class="section">
      <div class="section-title">👃 Cultivo-Microscopía Nasal</div>
      <div class="checkbox-list">
        ${formData.nasalEstornudos ? '<div>✓ Estornudos</div>' : ''}
        ${formData.nasalSecrecion ? '<div>✓ Secreción o congestión nasal</div>' : ''}
        ${formData.nasalCuadroRecurrente ? '<div>✓ Cuadro respiratorio recurrente</div>' : ''}
        ${formData.nasalSinAlteraciones ? '<div>✓ Fosas nasales sin alteraciones visibles</div>' : ''}
      </div>
    </div>
    ` : ''}
    
    ${(formData.coprDiarreaCronica || formData.coprDiarreaAguda || formData.coprDolorAbdominal || formData.coprVomito || formData.coprLetargia || formData.coprFiebre || formData.coprIngestaSospechosa) ? `
    <div class="section">
      <div class="section-title">💩 Coprocultivo</div>
      <div class="checkbox-list">
        ${formData.coprDiarreaCronica ? '<div>✓ Diarrea crónica</div>' : ''}
        ${formData.coprDiarreaAguda ? '<div>✓ Diarrea aguda/reciente</div>' : ''}
        ${formData.coprDolorAbdominal ? '<div>✓ Signos compatibles con dolor abdominal</div>' : ''}
        ${formData.coprVomito ? '<div>✓ Vómito</div>' : ''}
        ${formData.coprLetargia ? '<div>✓ Letargia/decaimiento</div>' : ''}
        ${formData.coprFiebre ? '<div>✓ Fiebre documentada (MVZ)</div>' : ''}
      </div>
      ${formData.coprIngestaSospechosa ? `<div class="field"><span class="field-label">Ingesta sospechosa:</span><span class="field-value">${formData.coprIngestaSospechosa}</span></div>` : ''}
      ${formData.coprCualIngesta ? `<div class="field"><span class="field-label">Cuál:</span><span class="field-value">${formData.coprCualIngesta}</span></div>` : ''}
    </div>
    ` : ''}
    
    ${(formData.uroAntecedenteInfeccion || formData.uroOlorFuerte || formData.uroDolorAbdominal || formData.uroDolorPalpacion || formData.uroEsfuerzoOrinar || formData.uroMiccionesFrecuentes || formData.uroSecrecionUretral || formData.uroOrinaTurbia) ? `
    <div class="section">
      <div class="section-title">🔬 Urocultivo-Microscopía de Sedimento Urinario</div>
      ${formData.uroAntecedenteInfeccion ? `<div class="field"><span class="field-label">Antecedente de infección urinaria:</span><span class="field-value">${formData.uroAntecedenteInfeccion}</span></div>` : ''}
      ${formData.uroOlorFuerte ? `<div class="field"><span class="field-label">Olor fuerte en orina:</span><span class="field-value">${formData.uroOlorFuerte}</span></div>` : ''}
      <div class="checkbox-list">
        ${formData.uroDolorAbdominal ? '<div>✓ Signos compatibles con dolor abdominal o lumbar</div>' : ''}
        ${formData.uroDolorPalpacion ? '<div>✓ Dolor abdominal a la palpación (MVZ)</div>' : ''}
        ${formData.uroEsfuerzoOrinar ? '<div>✓ Esfuerzo o dolor aparente al orinar</div>' : ''}
        ${formData.uroMiccionesFrecuentes ? '<div>✓ Micciones frecuentes con poco volumen</div>' : ''}
        ${formData.uroSecrecionUretral ? '<div>✓ Secreción uretral o vaginal</div>' : ''}
        ${formData.uroOrinaTurbia ? '<div>✓ Orina turbia o sangre visible en orina</div>' : ''}
      </div>
    </div>
    ` : ''}
    
    ${(formData.hongosTipoLesion || formData.hongosSitioLesion) ? `
    <div class="section">
      <div class="section-title">🍄 Cultivo-Microscopía Búsqueda de Hongos</div>
      ${formData.hongosTipoLesion ? `<div class="field"><span class="field-label">Tipo de lesión:</span><span class="field-value">${formData.hongosTipoLesion}</span></div>` : ''}
      ${formData.hongosSitioLesion ? `<div class="field"><span class="field-label">Sitio de lesión:</span><span class="field-value">${formData.hongosSitioLesion}</span></div>` : ''}
    </div>
    ` : ''}
    
    ${(formData.traquealTosCronica || formData.traquealTosReciente || formData.traquealDificultadRespiratoria || formData.traquealRespiracionRapida || formData.traquealPerdidaPeso || formData.traquealFiebre) ? `
    <div class="section">
      <div class="section-title">🫁 Cultivo-Microscopía Traqueal/Bronquial</div>
      <div class="checkbox-list">
        ${formData.traquealTosCronica ? '<div>✓ Tos crónica</div>' : ''}
        ${formData.traquealTosReciente ? '<div>✓ Tos reciente</div>' : ''}
        ${formData.traquealDificultadRespiratoria ? '<div>✓ Dificultad respiratoria (disnea)</div>' : ''}
        ${formData.traquealRespiracionRapida ? '<div>✓ Respiración rápida (taquipnea)</div>' : ''}
        ${formData.traquealPerdidaPeso ? '<div>✓ Pérdida de peso</div>' : ''}
        ${formData.traquealFiebre ? '<div>✓ Fiebre documentada (MVZ)</div>' : ''}
      </div>
    </div>
    ` : ''}
    
  </div>
</body>
</html>
    `;
  };

  const handleKeyPress = (e) => {
    // Prevenir submit con Enter en cualquier campo excepto textareas
    if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') {
      e.preventDefault();
    }
  };

  const handleSubmit = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    // Verificar que estamos en el paso 4 y el usuario realmente quiere enviar
    if (currentStep !== 4) {
      console.log('Submit bloqueado: no estamos en el paso 4');
      return;
    }
    
    setIsSubmitting(true);

    try {
      // Guardar datos para la página de confirmación
      sessionStorage.setItem('labRequestData', JSON.stringify(formData));
      
      // Enviar datos al script PHP
      const response = await fetch('/enviar-solicitud.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      // Verificar si la respuesta es exitosa antes de leer el body
      if (!response.ok) {
        throw new Error(`Error del servidor: ${response.status}`);
      }

      // Leer el body solo una vez
      const result = await response.json();

      if (result.success) {
        // Redirigir a página de confirmación
        navigate('/confirmacion');
      } else {
        throw new Error(result.message || 'Error al enviar el formulario');
      }
    } catch (error) {
      console.error('Error al enviar el formulario:', error);
      
      // Mensaje de error más específico
      let errorMessage = 'Hubo un error al enviar el formulario. Por favor, intenta nuevamente.';
      
      if (error.message.includes('404')) {
        // En desarrollo, el archivo PHP no está disponible
        // Pero permitir continuar a la página de confirmación
        console.warn('Modo desarrollo: archivo PHP no disponible, mostrando confirmación de todos modos');
        navigate('/confirmacion');
        return;
      } else if (error.message.includes('Failed to fetch')) {
        errorMessage = 'No se pudo conectar con el servidor. Verifica tu conexión a internet.';
      } else if (error.message) {
        errorMessage += '\n\nDetalle: ' + error.message;
      }
      
      alert(errorMessage);
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <img 
            src={logo} 
            alt="Amplicare Lab" 
            className="h-16 mx-auto mb-4"
          />
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            Solicitud de Pruebas de Laboratorio
          </h1>
          <p className="text-gray-600">
            Complete el formulario para solicitar sus pruebas de laboratorio
          </p>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex justify-between mb-2">
            {stepTitles.map((title, index) => (
              <div 
                key={index}
                className={`text-sm font-medium ${
                  currentStep > index + 1 
                    ? 'text-green-600' 
                    : currentStep === index + 1 
                    ? 'text-[#505BB3]' 
                    : 'text-gray-400'
                }`}
              >
                <div className="flex flex-col items-center">
                  <div 
                    className={`w-8 h-8 rounded-full flex items-center justify-center mb-1 ${
                      currentStep > index + 1
                        ? 'bg-green-600 text-white'
                        : currentStep === index + 1
                        ? 'bg-[#505BB3] text-white'
                        : 'bg-gray-200 text-gray-400'
                    }`}
                  >
                    {index + 1}
                  </div>
                  <span className="hidden sm:inline text-center">{title}</span>
                </div>
              </div>
            ))}
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Form Card */}
        <Card className="shadow-lg">
          <CardHeader className="bg-[#505BB3] text-white rounded-t-lg">
            <CardTitle className="text-xl">{stepTitles[currentStep - 1]}</CardTitle>
            <CardDescription className="text-gray-100">
              Paso {currentStep} de {totalSteps}
              {currentStep <= 3 && (
                <span className="ml-2 text-yellow-200">* Campos obligatorios</span>
              )}
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} onKeyDown={handleKeyPress}>
              {currentStep === 1 && (
                <Step1Veterinario formData={formData} updateFormData={updateFormData} />
              )}
              {currentStep === 2 && (
                <Step2Paciente formData={formData} updateFormData={updateFormData} />
              )}
              {currentStep === 3 && (
                <Step3Pruebas formData={formData} updateFormData={updateFormData} />
              )}
              {currentStep === 4 && (
                <Step4InfoClinica formData={formData} updateFormData={updateFormData} />
              )}

              {/* Navigation Buttons */}
              <div className="flex justify-between mt-8 pt-6 border-t">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handlePrevious}
                  disabled={currentStep === 1}
                  className="flex items-center gap-2"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Anterior
                </Button>

                {currentStep < totalSteps ? (
                  <Button
                    type="button"
                    onClick={handleNext}
                    className="flex items-center gap-2 bg-[#505BB3] hover:bg-[#404a99]">
                    Siguiente
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                ) : (
                  <Button
                    type="button"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    className="flex items-center gap-2 bg-green-600 hover:bg-green-700"
                  >
                    {isSubmitting ? 'Enviando...' : 'Enviar Solicitud'}
                    <Send className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default LabRequestForm;